import ProctorVerdict from "@/components/wishire/ProctorVerdict";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { practiceAllowance, purchasePracticePack, mockTestStart, mockTestSubmit, mockTestHistory } from "@/lib/api";
import { useFaceGuard, waitForFace } from "@/lib/faceGuard";
import { completePurchase } from "@/lib/razorpay";
import {
  ClipboardCheck, Loader2, Camera, CameraOff, ShieldCheck, ShieldAlert,
  Clock, CheckCircle2, XCircle, Sparkles, RotateCcw, Lock,
} from "lucide-react";

/* The agent examiner: generates a fresher-level paper for the student's
   target role, runs it against a 20-minute clock with camera + tab-switch
   proctoring, and grades it with per-question feedback. */

export function PaywallCard({ allowance, onPurchased }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [payErr, setPayErr] = useState(null);
  const pack = allowance?.pack || { price_inr: 399, interviews: 5, mock_tests: 5 };
  const buy = async () => {
    setBusy(true); setPayErr(null);
    try {
      const { data } = await purchasePracticePack();
      await completePurchase(data, { user }); // Razorpay when live, instant in mock mode
      onPurchased();
    } catch (e) { setPayErr(e?.message || e?.response?.data?.detail || "Payment failed."); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-3xl bg-stone-900 text-stone-50 p-8 relative overflow-hidden wsh-grain" data-testid="paywall">
      <div className="absolute inset-0 opacity-45 pointer-events-none" style={{ background: "radial-gradient(420px 260px at 90% 0%, rgba(217,119,6,0.45), transparent 60%)" }} />
      <div className="relative">
        <span className="inline-flex p-2.5 rounded-xl bg-amber-300 text-stone-900"><Lock size={18} /></span>
        <h2 className="font-display text-3xl mt-3">You've used your free sessions — and it showed you what's possible.</h2>
        <p className="mt-2 text-stone-300 max-w-lg">Students who practice 4–5 times move their Wishire Score by 15+ points — that's the difference between watching drives and getting shortlisted.</p>
        <div className="mt-5 rounded-2xl bg-white/10 border border-white/15 p-5 max-w-md">
          <p className="font-display text-2xl">Practice Pack — ₹{pack.price_inr}</p>
          <ul className="mt-2 space-y-1.5 text-sm text-stone-200">
            <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-amber-300" /> {pack.interviews} AI voice interviews with Naina</li>
            <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-amber-300" /> Full {pack.session_minutes || 12}-minute sessions (free tier: 6 min)</li>
            <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-amber-300" /> {pack.mock_tests} proctored mock tests with per-question feedback</li>
            <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-amber-300" /> Every attempt raises your Wishire Score accuracy</li>
          </ul>
          <button onClick={buy} disabled={busy} data-testid="paywall-buy"
            className="mt-4 w-full px-5 py-3 rounded-full bg-amber-300 text-stone-900 font-semibold hover:bg-amber-200 disabled:opacity-60">
            {busy ? <Loader2 size={16} className="animate-spin inline" /> : `Unlock for ₹${pack.price_inr}`}
          </button>
          {payErr && <p className="mt-2 text-xs text-red-300 text-center">{payErr}</p>}
        </div>
      </div>
    </div>
  );
}

export default function MockTestPage() {
  const { user } = useAuth();
  const [stage, setStage] = useState("intro"); // intro | live | grading | result
  const [allowance, setAllowance] = useState(null);
  const [role, setRole] = useState(user?.target_role || "Software Engineer");
  const [test, setTest] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [err, setErr] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(0);

  // Proctoring
  const [cameraActive, setCameraActive] = useState(false);
  const [tabSwitches, setTabSwitches] = useState(0);
  const webcamRef = useRef(null);
  const streamRef = useRef(null);
  const tabRef = useRef(0);
  const stageRef = useRef("intro");
  useEffect(() => { stageRef.current = stage; }, [stage]);
  const [faceGate, setFaceGate] = useState(null);
  const face = useFaceGuard(webcamRef, cameraActive, stage === "live");
  // The <video> lives in different cards per stage; re-attach the stream whenever it (re)mounts.
  useEffect(() => { if (cameraActive && webcamRef.current && streamRef.current && webcamRef.current.srcObject !== streamRef.current) webcamRef.current.srcObject = streamRef.current; }, [cameraActive, stage, faceGate]);
  const faceRef = useRef(face); useEffect(() => { faceRef.current = face; }, [face]);

  const loadMeta = () => {
    practiceAllowance().then(({ data }) => setAllowance(data)).catch(() => {});
    mockTestHistory().then(({ data }) => setHistory(data || [])).catch(() => {});
  };
  useEffect(() => { loadMeta(); return () => stopCamera(); }, []); // eslint-disable-line

  useEffect(() => {
    const onVis = () => {
      if (document.hidden && stageRef.current === "live") {
        tabRef.current += 1;
        setTabSwitches(tabRef.current);
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" }, audio: false });
      streamRef.current = stream;
      if (webcamRef.current) webcamRef.current.srcObject = stream;
      setCameraActive(true);
    } catch (_) { setCameraActive(false); }
  };
  const stopCamera = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setCameraActive(false);
  };

  const begin = async () => {
    setErr(null);
    try {
      await startCamera();
      if (!streamRef.current) { setErr("Turn your camera on to start the proctored test — your face must be visible, like a company's online assessment."); return; }
      setFaceGate("Looking for your face… face the camera in good light.");
      const seen = await waitForFace(() => faceRef.current.present, faceRef.current.supported ? 9000 : 1500);
      setFaceGate(null);
      if (!seen && faceRef.current.supported) { stopCamera(); setErr("We can't see your face. Sit facing the camera with light on your face, then try again."); return; }
      faceRef.current.reset();
      const { data } = await mockTestStart(role.trim());
      setTest(data); setAnswers({}); tabRef.current = 0; setTabSwitches(0);
      setSecondsLeft(data.duration_min * 60);
      setStage("live");
    } catch (e) {
      stopCamera();
      if (e?.response?.status === 402) { loadMeta(); setErr("paywall"); }
      else setErr(e?.response?.data?.detail || "Could not start the test — try again.");
    }
  };

  const finish = useCallback(async () => {
    setStage("grading");
    stopCamera();
    try {
      const { data } = await mockTestSubmit(test.test_id, {
        answers, tab_switches: tabRef.current, camera_active: streamRef.current !== null || cameraActive, ...faceRef.current.snapshot(),
      });
      setResult(data.result); setStage("result"); loadMeta();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Grading failed — your answers are saved; try submitting again.");
      setStage("live");
    }
  }, [test, answers, cameraActive]); // eslint-disable-line

  // Countdown
  useEffect(() => {
    if (stage !== "live") return;
    const t = setInterval(() => setSecondsLeft((s) => {
      if (s <= 1) { clearInterval(t); finish(); return 0; }
      return s - 1;
    }), 1000);
    return () => clearInterval(t);
  }, [stage, finish]);

  const mm = String(Math.floor(secondsLeft / 60)).padStart(2, "0");
  const ss = String(secondsLeft % 60).padStart(2, "0");
  const remaining = allowance?.mock_tests?.remaining ?? null;

  return (
    <div className="container-x py-10 md:py-14">
      <div className="mb-8">
        <span className="overline">Proctored mock test</span>
        <h1 className="font-display text-4xl md:text-5xl mt-2 leading-tight">The exam before <span className="italic text-stone-500">the exam.</span></h1>
        <p className="mt-3 text-stone-600 max-w-2xl">Your AI examiner writes a fresher-level paper for your target role, proctors you through 20 timed minutes (camera + tab tracking), and grades every answer with feedback.</p>
      </div>

      {stage === "intro" && (
        err === "paywall" || remaining === 0 ? (
          <PaywallCard allowance={allowance} onPurchased={() => { setErr(null); loadMeta(); }} />
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6 items-start">
            <div className="rounded-3xl border border-stone-200 bg-white p-7">
              <h2 className="font-display text-2xl">Before you start</h2>
              <ul className="mt-4 space-y-2.5 text-sm text-stone-700">
                <li className="flex gap-2.5"><ClipboardCheck size={17} className="text-[#C2410C] shrink-0 mt-0.5" /> 10 questions — 6 multiple-choice + 4 written — generated for <b>your</b> role at campus-drive difficulty.</li>
                <li className="flex gap-2.5"><Clock size={17} className="text-[#C2410C] shrink-0 mt-0.5" /> 20 minutes, auto-submits when time runs out.</li>
                <li className="flex gap-2.5"><Camera size={17} className="text-[#C2410C] shrink-0 mt-0.5" /> Proctored: camera stays on, tab switches are counted and shown on your report — exactly like a real online assessment.</li>
                <li className="flex gap-2.5"><Sparkles size={17} className="text-[#C2410C] shrink-0 mt-0.5" /> Instant grading with per-question feedback.</li>
              </ul>
              <div className="mt-4 rounded-2xl bg-[#F5F3ED] border border-stone-200 p-4 text-xs text-stone-600 leading-relaxed" data-testid="mt-privacy">
                <p className="font-medium text-stone-800 text-sm">What we record, and why</p>
                <p className="mt-1">Your camera stays on so the report can confirm it was you — the same as a company's online test. We keep a few low-resolution snapshots and a count of tab switches, nothing else; no audio, no screen recording. Your placement cell sees the integrity note on your report, never the images.</p>
                <p className="mt-1">Your face must be visible to start and stay visible during the test — if we can't see you, the attempt is marked unverified and counts for less in your Wishire Score.</p>
              </div>
              <div className="mt-5">
                <label className="text-xs font-medium text-stone-500">Target role</label>
                <input value={role} onChange={(e) => setRole(e.target.value)} data-testid="mt-role"
                  className="mt-1 w-full max-w-sm px-4 py-2.5 rounded-xl border border-stone-300 text-sm focus:outline-none focus:border-stone-900" />
              </div>
              {cameraActive && stage !== "live" && (
                <div className="mt-4 flex items-center gap-4" data-testid="mt-camera-check">
                  <video ref={webcamRef} autoPlay muted playsInline className="w-40 rounded-xl bg-stone-900 aspect-video object-cover border border-stone-200" />
                  <p className={`text-sm ${face.present ? "text-emerald-700" : "text-stone-500"}`}>{face.present ? "✓ Face visible — starting…" : "Camera check: face the camera in good light."}</p>
                </div>
              )}
              {faceGate && <p className="mt-4 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-4 py-2" data-testid="face-gate">{faceGate}</p>}
              {err && err !== "paywall" && <p className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-2">{err}</p>}
              <button onClick={begin} data-testid="mt-start"
                className="mt-5 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-stone-900 text-white font-medium hover:bg-stone-700">
                <ShieldCheck size={16} /> Start proctored test
              </button>
              {remaining != null && (
                <p className="mt-3 text-xs text-stone-500" data-testid="mt-remaining">
                  {remaining} proctored test{remaining !== 1 ? "s" : ""} left
                  {allowance?.interviews && ` · ${allowance.interviews.remaining} voice interview${allowance.interviews.remaining !== 1 ? "s" : ""} left`}
                  {allowance?.pack && <span className="block mt-1 text-stone-400" data-testid="mt-allowance-note">Free sessions come with your college licence · the Practice Pack adds {allowance.pack.interviews} interviews + {allowance.pack.mock_tests} tests for ₹{allowance.pack.price_inr}.</span>}
                </p>
              )}
            </div>
            <div className="rounded-3xl border border-stone-200 bg-white p-6">
              <span className="overline">Your past tests</span>
              {history.length === 0 ? (
                <p className="mt-2 text-sm text-stone-500">No tests yet — your first one sets the baseline.</p>
              ) : (
                <div className="mt-3 space-y-2">
                  {history.slice(0, 6).map((h) => (
                    <div key={h.test_id} className="flex items-center justify-between rounded-xl border border-stone-100 px-3 py-2">
                      <div><p className="text-sm font-medium">{h.role}</p><p className="text-[11px] text-stone-400">{new Date(h.started_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}{h.result?.integrity === "flagged" && <span className="text-red-500 ml-1">· flagged</span>}</p></div>
                      <span className={`font-mono font-bold ${h.result?.score >= 70 ? "text-emerald-600" : h.result?.score >= 45 ? "text-amber-600" : "text-red-600"}`}>{h.result?.score}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )
      )}

      {stage === "live" && test && (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-6 items-start">
          <div className="space-y-4">
            {test.mcq.map((q, i) => (
              <div key={i} className="rounded-2xl border border-stone-200 bg-white p-5" data-testid={`mt-q-${i}`}>
                <p className="text-sm font-medium text-stone-900"><span className="text-stone-400 font-mono mr-2">Q{i + 1}</span>{q.q}</p>
                <div className="mt-3 grid sm:grid-cols-2 gap-2">
                  {q.options.map((op, oi) => (
                    <label key={oi} className={`flex items-center gap-2.5 px-3 py-2 rounded-xl border text-sm cursor-pointer ${answers[String(i)] === oi ? "border-stone-900 bg-stone-50 font-medium" : "border-stone-200 hover:border-stone-400"}`}>
                      <input type="radio" name={`q${i}`} checked={answers[String(i)] === oi}
                        onChange={() => setAnswers((a) => ({ ...a, [String(i)]: oi }))} className="accent-[#C2410C]" />
                      {op}
                    </label>
                  ))}
                </div>
              </div>
            ))}
            {test.short.map((q, j) => {
              const key = String(test.mcq.length + j);
              return (
                <div key={key} className="rounded-2xl border border-stone-200 bg-white p-5" data-testid={`mt-q-${key}`}>
                  <p className="text-sm font-medium text-stone-900"><span className="text-stone-400 font-mono mr-2">Q{test.mcq.length + j + 1}</span>{q.q}</p>
                  <textarea rows={3} value={answers[key] || ""} onChange={(e) => setAnswers((a) => ({ ...a, [key]: e.target.value }))}
                    placeholder="Type your answer…" className="mt-3 w-full px-3 py-2 rounded-xl border border-stone-300 text-sm focus:outline-none focus:border-stone-900" />
                </div>
              );
            })}
            <button onClick={finish} data-testid="mt-submit"
              className="w-full px-6 py-3.5 rounded-full bg-stone-900 text-white font-medium hover:bg-stone-700">
              Submit for grading
            </button>
          </div>

          {/* Proctor rail */}
          <div className="lg:sticky lg:top-6 space-y-3">
            <div className={`rounded-2xl p-4 text-center font-mono text-3xl font-bold ${secondsLeft < 120 ? "bg-red-50 text-red-600 border border-red-200" : "bg-stone-900 text-amber-300"}`} data-testid="mt-timer">
              {mm}:{ss}
            </div>
            <div className="rounded-2xl border border-stone-200 bg-white p-3">
              <video ref={webcamRef} autoPlay muted playsInline className="w-full rounded-xl bg-stone-900 aspect-video object-cover" />
              <div className="mt-2 flex items-center justify-between text-xs">
                <span className={`inline-flex items-center gap-1 ${cameraActive ? "text-emerald-600" : "text-red-600"}`}>
                  {cameraActive ? <Camera size={12} /> : <CameraOff size={12} />} {cameraActive ? (face.ready ? (face.present ? (face.faces > 1 ? `${face.faces} faces in frame` : "Face visible") : "We can't see your face") : "Proctoring on") : "Camera off"}
                </span>
                <span className={`inline-flex items-center gap-1 ${tabSwitches === 0 ? "text-emerald-600" : "text-red-600"}`}>
                  {tabSwitches === 0 ? <ShieldCheck size={12} /> : <ShieldAlert size={12} />} {tabSwitches} switch{tabSwitches !== 1 ? "es" : ""}
                </span>
              </div>
            </div>
            <p className="text-[11px] text-stone-400 px-1">Leaving this tab is recorded, exactly like a company assessment.</p>
          </div>
        </div>
      )}

      {stage === "grading" && (
        <div className="rounded-3xl border border-stone-200 bg-white p-12 text-center">
          <Loader2 size={28} className="animate-spin mx-auto text-[#C2410C]" />
          <p className="mt-4 font-display text-2xl">Your examiner is grading…</p>
          <p className="text-sm text-stone-500 mt-1">Checking every answer and writing your feedback.</p>
        </div>
      )}

      {stage === "result" && result && (
        <div>
          <div className="rounded-3xl bg-stone-900 text-stone-50 p-7 relative overflow-hidden wsh-grain mb-6" data-testid="mt-result">
            <div className="absolute inset-0 opacity-40 pointer-events-none" style={{ background: "radial-gradient(420px 240px at 90% 0%, rgba(217,119,6,0.4), transparent 60%)" }} />
            <div className="relative flex flex-wrap items-center justify-between gap-4">
              <div>
                <span className="overline" style={{ color: "rgba(253,251,247,0.55)" }}>Result · {test?.role}</span>
                <p className="font-display text-6xl mt-1">{result.score}<span className="text-2xl text-stone-400">/100</span></p>
                <p className="text-stone-300 mt-1">{result.verdict}</p>
              </div>
              <div className="text-sm space-y-1.5 text-stone-300">
                <p>MCQs: <b className="text-stone-50">{result.mcq_correct}/{result.mcq_total}</b></p>
                <p>Written: <b className="text-stone-50">{result.short_score}/{result.short_total}</b></p>
                <ProctorVerdict p={result.proctor} integrity={result.integrity} dark />
                {result.counted === false && <p className="text-amber-300" data-testid="mt-uncounted">Not counted toward your Wishire Score: {result.counted_reason}</p>}
                {result.time_expired && <p className="text-amber-300">Time expired</p>}
              </div>
            </div>
          </div>
          <div className="space-y-3">
            {result.mcq_results.map((r, i) => (
              <div key={i} className={`rounded-2xl border px-5 py-3.5 ${r.correct ? "border-emerald-200 bg-emerald-50/50" : "border-red-200 bg-red-50/50"}`}>
                <p className="text-sm font-medium flex items-start gap-2">
                  {r.correct ? <CheckCircle2 size={15} className="text-emerald-600 mt-0.5 shrink-0" /> : <XCircle size={15} className="text-red-500 mt-0.5 shrink-0" />}
                  {r.q}
                </p>
                {!r.correct && <p className="text-xs text-stone-600 mt-1 ml-6">You: {r.your_answer || "—"} · Correct: <b>{r.correct_answer}</b></p>}
              </div>
            ))}
            {result.short_results.map((r, i) => (
              <div key={i} className="rounded-2xl border border-stone-200 bg-white px-5 py-3.5">
                <div className="flex items-start justify-between gap-3">
                  <p className="text-sm font-medium">{r.q}</p>
                  <span className={`shrink-0 font-mono font-bold ${r.score >= 7 ? "text-emerald-600" : r.score >= 4 ? "text-amber-600" : "text-red-600"}`}>{r.score}/10</span>
                </div>
                <p className="text-xs text-stone-600 mt-1.5">{r.feedback}</p>
              </div>
            ))}
          </div>
          <button onClick={() => { setStage("intro"); setResult(null); setTest(null); loadMeta(); }}
            className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-stone-300 text-sm font-medium hover:border-stone-900">
            <RotateCcw size={15} /> Take another
          </button>
        </div>
      )}
    </div>
  );
}
